import { api } from '@/lib/api'

/**
 * Web Push plumbing for the staff/admin phone notifications. Everything here
 * runs in the window, never the service worker - the API token lives in
 * localStorage, which a worker cannot read, and the worker never needs it.
 *
 * iOS rules that shape this module: push only works when the app is added to
 * the Home Screen and opened from that icon (16.4+), and the permission
 * prompt is only allowed inside a user gesture - so enablePush() must be
 * called from a click handler, never on mount.
 */

export type PushStatus = 'unsupported' | 'need-install' | 'denied' | 'subscribed' | 'available'

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

function isIos(): boolean {
  // iPadOS reports itself as Mac; the touch check catches it.
  return (
    /iPhone|iPad|iPod/.test(navigator.userAgent) ||
    (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1)
  )
}

/** Running as the installed app (Home Screen / WebAPK) rather than in a browser tab. */
export function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // Safari's pre-standard flag, still what iOS sets for Home Screen apps.
    ('standalone' in navigator && (navigator as { standalone?: boolean }).standalone === true)
  )
}

/** iOS in a Safari tab: push exists only after Add to Home Screen. */
export function iosNeedsInstall(): boolean {
  return isIos() && !isStandalone()
}

export async function getPushStatus(): Promise<PushStatus> {
  if (iosNeedsInstall()) return 'need-install'
  if (!pushSupported()) return 'unsupported'
  if (Notification.permission === 'denied') return 'denied'

  try {
    const registration = await navigator.serviceWorker.getRegistration('/')
    const subscription = await registration?.pushManager.getSubscription()
    return subscription ? 'subscribed' : 'available'
  } catch {
    // Some browsers throw here when their push service is unreachable
    // (Firefox: "Error retrieving push subscription"). Offer the button
    // anyway - enablePush() surfaces the real error as a toast, which
    // beats hiding the controls with no explanation.
    return 'available'
  }
}

/**
 * Register the worker without subscribing. Called on portal mount so a device
 * that already granted push reattaches an updated sw.js; inert otherwise.
 */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return
  navigator.serviceWorker.register('/sw.js').catch(() => {
    // Registration failing (private mode, storage off) only means no push;
    // the app itself is unaffected.
  })
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const output = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i)
  return output
}

/** Must run inside a click handler - see the iOS gesture rule above. */
export async function enablePush(): Promise<void> {
  const registration = await navigator.serviceWorker.register('/sw.js')
  await navigator.serviceWorker.ready

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error('Notification permission was not granted.')

  const { data } = await api.get<{ key: string | null }>('/push/key')
  if (!data.key) throw new Error('Push notifications are not configured on the server.')

  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.key),
    }))

  await api.post('/push/subscriptions', subscription.toJSON())
}

/** Removes this device only; the user's other devices keep their push. */
export async function disablePush(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration('/')
  const subscription = await registration?.pushManager.getSubscription()
  if (!subscription) return

  await api.delete('/push/subscriptions', { data: { endpoint: subscription.endpoint } })
  await subscription.unsubscribe()
}
